import './applianceFilters.scss';

import { Range, getTrackBackground } from 'react-range';

import React from 'react';
import arrowIconBlack from '@progymedia/sbi-web-occ/src/images/icons/Icon-Fleche-Black.svg';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useProductsPrices } from '../../../../gatsby-theme-boutik-ecommerce/src/context/store';
import { useTranslation } from 'react-i18next';

export default function ApplianceFilters(props: {
    setFilteredAppliances: React.Dispatch<any>;
    filterValues: {
        priceValues: number[];
        areaValues: number[];
        powerValues: number[];
    };
    setFilterValues: React.Dispatch<
        React.SetStateAction<{
            priceValues: number[];
            areaValues: number[];
            powerValues: number[];
        }>
    >;
    defaults: {
        minVal: number;
        maxPrice: number;
        maxArea: number;
        maxPower: number;
    };
    applianceList: any;
    isFilterOpen: boolean;
    setFiltersOpen: React.Dispatch<React.SetStateAction<boolean>>;
    isMobile: boolean;
}) {
    const { t } = useTranslation('templatePages');
    const { currentLocale } = usePageLocalization();
    const {
        setFilteredAppliances,
        filterValues,
        setFilterValues,
        defaults,
        applianceList,
        isFilterOpen,
        setFiltersOpen,
        isMobile,
    } = props;

    let prices = useProductsPrices(
        applianceList.map((family: any) => family.code)
    );

    const SingleFilter = (props: { filterName: string; unit: string }) => {
        const { filterName, unit } = props;
        const step = 10;
        let minMax = getMinMax(filterName);
        const [currentValues, setCurrentValues] = React.useState(
            getState(filterName)
        );
        return (
            <div className='single-filter'>
                <h4 className='filter-name'>{filterName}</h4>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                    }}
                >
                    <Range
                        values={currentValues}
                        step={step}
                        min={minMax[0]}
                        max={minMax[1]}
                        onChange={(values) => {
                            setCurrentValues(values);
                        }}
                        renderTrack={({ props, children }) => (
                            <div
                                className='track'
                                onMouseDown={props.onMouseDown}
                                onTouchStart={props.onTouchStart}
                            >
                                <div
                                    className='track-bg'
                                    ref={props.ref}
                                    style={{
                                        background: getTrackBackground({
                                            values: currentValues,
                                            colors: ['#ccc', '#fd4239', '#ccc'],
                                            min: minMax[0],
                                            max: minMax[1],
                                        }),
                                    }}
                                >
                                    {children}
                                </div>
                            </div>
                        )}
                        renderThumb={({ props, isDragged }) => (
                            <div {...props} className='thumb'>
                                <div className='outter-thumb' />
                            </div>
                        )}
                        onFinalChange={(values) => {
                            handleSetValues(filterName, values, prices[0]);
                        }}
                    />

                    <div className='values'>
                        <span className='value leftValue'>{`${currentValues[0]}${unit}`}</span>
                        <span className='value rightValue'>{`${currentValues[1]}${unit}`}</span>
                    </div>
                    {/*  <output style={{ marginTop: '30px' }} id='output'></output> */}
                </div>
            </div>
        );
    };

    return (
        <div
            className='appliance-filter'
            id='appliance-filter'
            onMouseOver={() => {
                setFiltersOpen(true);
            }}
            onMouseLeave={() => {
                setFiltersOpen(false);
            }}
            onClick={() => {
                if (isMobile) {
                    setFiltersOpen(!isFilterOpen);
                }
            }}
        >
            <span className='appliance-filter-arrow'>
                <img src={arrowIconBlack} style={{ height: '12px' }} alt='' />
            </span>
            <span className='search-title'>
                {t('appliances.search-filters')}
            </span>

            {isFilterOpen && (
                <div className='appliance-filters'>
                    <h4 className='title'>
                        {t('filters.title')}
                        <span
                            className='reload-icon'
                            onClick={() => {
                                setFilterValues({
                                    priceValues: [
                                        defaults.minVal,
                                        defaults.maxPrice,
                                    ],
                                    areaValues: [
                                        defaults.minVal,
                                        defaults.maxArea,
                                    ],
                                    powerValues: [
                                        defaults.minVal,
                                        defaults.maxPower,
                                    ],
                                });
                                setFilteredAppliances(applianceList);
                            }}
                        >
                            {t('filters.reset')} &#x21bb;
                        </span>
                    </h4>
                    <div className='filters'>
                        <SingleFilter
                            filterName={t('filters.price')}
                            unit={currentLocale === 'en_US' ? '$ USD' : ' CAD$'}
                        />
                        <SingleFilter
                            filterName={t('filters.square-foot')}
                            unit={currentLocale === 'fr_CA' ? ' pi²' : ' ft²'}
                        />

                        <SingleFilter
                            filterName={t('filters.power')}
                            unit=' BTU/h'
                        />
                    </div>
                </div>
            )}
        </div>
    );

    function handleSetValues(
        filterName: string,
        values: number[],
        prices: any
    ): void {
        let newVal = filterValues;
        switch (filterName) {
            case t('filters.price'):
                newVal = { ...filterValues, priceValues: values };
                setFilterValues(newVal);
                setFilteredAppliances(
                    applianceList
                        .filter((product: any, index: number) =>
                            filterByPrice(product, prices, values, index)
                        )
                        .filter((product: any) =>
                            filterByArea(product, newVal.areaValues)
                        )
                        .filter((product: any) =>
                            filterByPower(product, newVal.powerValues)
                        )
                );
                break;
            case t('filters.square-foot'):
                newVal = { ...filterValues, areaValues: values };
                setFilterValues(newVal);
                setFilteredAppliances(
                    applianceList
                        .filter((product: any) => filterByArea(product, values))
                        .filter((product: any, index: number) =>
                            filterByPrice(
                                product,
                                prices,
                                newVal.priceValues,
                                index
                            )
                        )
                        .filter((product: any) =>
                            filterByPower(product, newVal.powerValues)
                        )
                );
                break;
            case t('filters.power'):
                newVal = { ...filterValues, powerValues: values };
                setFilterValues(newVal);
                setFilteredAppliances(
                    applianceList
                        .filter((product: any) =>
                            filterByPower(product, values)
                        )
                        .filter((product: any) =>
                            filterByArea(product, newVal.areaValues)
                        )
                        .filter((product: any, index: number) =>
                            filterByPrice(
                                product,
                                prices,
                                newVal.priceValues,
                                index
                            )
                        )
                );
                break;
        }
    }
    function getState(filterName: string): number[] {
        switch (filterName) {
            case t('filters.price'):
                return filterValues.priceValues;
            case t('filters.square-foot'):
                return filterValues.areaValues;
            case t('filters.power'):
                return filterValues.powerValues;
            default:
                return [0, 1000];
        }
    }
    function getMinMax(filterName: string): number[] {
        switch (filterName) {
            case t('filters.price'):
                return [defaults.minVal, defaults.maxPrice];
            case t('filters.square-foot'):
                return [defaults.minVal, defaults.maxArea];
            case t('filters.power'):
                return [defaults.minVal, defaults.maxPower];
            default:
                return [0, 10000];
        }
    }
    function filterByPrice(
        product: any,
        prices: any,
        value: number[],
        index: number
    ) {
        const regularPrice: number = prices[index]?.regularPrice?.value
            ? prices[index]?.regularPrice?.value
            : -1;
        if (regularPrice <= value[1] && regularPrice >= value[0]) {
            return product;
        }
    }
    function filterByArea(product: any, values: number[]) {
        const productArea: string =
            product.attributes?.Surfi?.values[0]?.[currentLocale];
        let productMin = 0,
            productMax = 10000;
        if (productArea && productArea.includes('< ')) {
            productMin = parseInt(productArea.split('< ')[1].replace(',', ''));
            productMax = parseInt(productArea.split('< ')[1].replace(',', ''));
        } else if (productArea && productArea.includes(' - ')) {
            productMin = parseInt(productArea.split(' - ')[0].replace(',', ''));
            productMax = parseInt(productArea.split(' - ')[1].replace(',', ''));
        }
        const available_countries =
            product.attributes?.available_country?.values;

        const current_country =
            currentLocale.split('_')[1] === 'CA' ? 'Canada' : 'United States';

        const is_available_current_country =
            available_countries === undefined
                ? true
                : available_countries.some(
                      (country: any) =>
                          country[currentLocale] === current_country
                  );
        return (
            is_available_current_country &&
            ((productMax <= values[1] && productMin >= values[0]) ||
                !productArea)
        );
    }
    function filterByPower(product: any, values: number[]) {
        let productPower = parseInt(
            product.attributes?.PWMaSoi?.values[0]?.[currentLocale]
                .split(' ')[0]
                .replace(',', '')
        );

        if (isNaN(productPower) || !productPower) {
            productPower = 0;
        }

        const available_countries =
            product.attributes?.available_country?.values;

        const current_country =
            currentLocale.split('_')[1] === 'CA' ? 'Canada' : 'United States';

        const is_available_current_country =
            available_countries === undefined
                ? true
                : available_countries.some(
                      (country: any) =>
                          country[currentLocale] === current_country
                  );
        return (
            is_available_current_country &&
            productPower >= values[0] &&
            productPower <= values[1]
        );
    }
}
